import styled, { css, keyframes } from "styled-components";
import { useAdmin } from "../../../context/admin";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { format, subDays } from "date-fns";
import { Agency, CashAccount, CashCategory, CashFlowType, CashPendingPayment, CashShift, TraspassDebt, ReservationSearchResult, GroupSummary, ExistingPayment, Seller } from "../../../types/common.types";
import AdminService from "../../../services/admin.service";
import GeneralInput from "../../common/GeneralInput";
import Select from "../../common/Select";
import FileInput from "../../common/FileInput";
import { useGlobal } from "../../../context/global";
import { mobile } from "../../../styles/responsive";

export default function CreateCashMovement() {
    const { setIsLoading } = useGlobal()
    const { setIsCreateCashMovementModalOpen, createCashMovementPrefill, setCreateCashMovementPrefill } = useAdmin()
    const prefill = createCashMovementPrefill
    const [accounts, setAccounts] = useState<CashAccount[]>([])
    const [categories, setCategories] = useState<CashCategory[]>([])
    const [shifts, setShifts] = useState<CashShift[]>([])
    const [pendingPayments, setPendingPayments] = useState<CashPendingPayment[]>([])
    const [accountId, setAccountId] = useState(prefill?.account_id || '')
    const [movementType, setMovementType] = useState<CashFlowType>(prefill?.movement_type || 'egreso')
    const [amount, setAmount] = useState(prefill?.amount ? String(prefill.amount) : '')
    const [categoryId, setCategoryId] = useState(prefill?.category_id || '')
    const [counterpartName, setCounterpartName] = useState(prefill?.counterpart_name || '')
    const [detail, setDetail] = useState(prefill?.detail || '')
    // Fecha en que el movimiento ocurrió realmente (para saldos por semana/rango) — separada de
    // cuándo se registra en el sistema. Por defecto hoy; si viene de conciliar una línea de banco,
    // ya llega precargada con la fecha real del extracto. Editable para carga de historial atrasado.
    const [executionDate, setExecutionDate] = useState(prefill?.execution_date ? String(prefill.execution_date).slice(0, 10) : format(new Date(), 'yyyy-MM-dd'))
    const [photo, setPhoto] = useState<File | undefined>()
    const [shiftId, setShiftId] = useState(prefill?.shift_id || '')
    const [pendingPaymentId, setPendingPaymentId] = useState(prefill?.pending_payment_id || '')
    const tripCostDebtId = prefill?.trip_cost_debt_id
    const [agencies, setAgencies] = useState<Agency[]>([])
    const [traspassAgencyId, setTraspassAgencyId] = useState(prefill?.counterpart_agency_id || '')
    const [agencySearch, setAgencySearch] = useState('')
    const [isAgencySearchOpen, setIsAgencySearchOpen] = useState(false)
    const [traspassDebts, setTraspassDebts] = useState<TraspassDebt[]>([])
    const [selectedLinks, setSelectedLinks] = useState<Record<string, { tour: number, tickets: number }>>({})
    const [debtsFrom, setDebtsFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'))
    const [debtsTo, setDebtsTo] = useState(format(new Date(), 'yyyy-MM-dd'))
    // Vincular a un pago (`payments`) que ya existe en el sistema, en vez de crear uno nuevo — caso
    // real: el pago se registró antes por la pestaña Traspasos, o viene de datos históricos.
    const [existingPaymentSearch, setExistingPaymentSearch] = useState('')
    const [isExistingPaymentSearchOpen, setIsExistingPaymentSearchOpen] = useState(false)
    const [existingPaymentResults, setExistingPaymentResults] = useState<ExistingPayment[]>([])
    const [selectedExistingPayments, setSelectedExistingPayments] = useState<Record<string, ExistingPayment>>({})
    const [reservationSearch, setReservationSearch] = useState('')
    const [isReservationSearchOpen, setIsReservationSearchOpen] = useState(false)
    const [reservationResults, setReservationResults] = useState<ReservationSearchResult[]>([])
    const [selectedReservation, setSelectedReservation] = useState<ReservationSearchResult | null>(null)
    const [reservationPayments, setReservationPayments] = useState<any[]>([])
    const [paymentReservationId, setPaymentReservationId] = useState('')
    const [ventaAmountTour, setVentaAmountTour] = useState('')
    const [ventaAmountTickets, setVentaAmountTickets] = useState('0')
    const [isGroupMode, setIsGroupMode] = useState(false)
    const [groupSummary, setGroupSummary] = useState<GroupSummary | null>(null)
    // Abono contra la deuda TOTAL de la agencia (ej. "$50.000 abono a la cuenta de Borialis"), sin
    // corresponder a un pasajero/traspaso puntual — crea un `payments` general.
    const [isGeneralAbono, setIsGeneralAbono] = useState(false)
    // "Deuda / Anticipo Interno" necesita quedar vinculado al vendedor REAL (counterpart_seller_id),
    // no solo un nombre libre — es lo que lee listUnsettledAdvancesForSeller para restarlo de su pago
    // semanal. Antes solo existía el campo de texto "Contraparte", así que nunca se descontaba solo.
    const [sellers, setSellers] = useState<Seller[]>([])
    const [advanceSellerId, setAdvanceSellerId] = useState('')

    useEffect(() => {
        async function loadData() {
            setIsLoading(true)
            try {
                const [accountsRes, categoriesRes, pendingRes, agenciesRes, sellersRes] = await Promise.all([
                    AdminService.listCashAccounts(),
                    AdminService.listCashCategories(),
                    AdminService.listCashPendingPayments('open'),
                    AdminService.listAgencies(),
                    AdminService.listSellers()
                ])
                setAccounts(accountsRes.data)
                setCategories(categoriesRes.data)
                setPendingPayments(pendingRes.data)
                setAgencies(agenciesRes.data)
                setSellers(sellersRes.data)
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Turnos del día del MOVIMIENTO (executionDate), no de "currentDay" (un estado global de admin
    // ajeno a esta pantalla, que queda desincronizado de la fecha realmente seleccionada en Caja) —
    // ese desacople hacía que el selector de Turno mostrara gente de una fecha completamente
    // distinta a la que se estaba cargando. Se recarga cada vez que cambia la fecha del movimiento.
    useEffect(() => {
        async function loadShifts() {
            if (!executionDate) { setShifts([]); return }
            try {
                const res = await AdminService.listCashShifts(executionDate, executionDate)
                setShifts(res.data)
            } catch (error) {
                console.log(error)
            }
        }
        loadShifts()
    }, [executionDate])

    const filteredCategories = categories.filter(c => c.flow_type === movementType).sort((a, b) => a.name.localeCompare(b.name))
    const selectedCategory = categories.find(c => c.id === categoryId)
    const isTraspaso = !tripCostDebtId && (selectedCategory?.name || '').startsWith('Traspasos')
    const isVenta = !tripCostDebtId && !isTraspaso && movementType === 'ingreso' && (selectedCategory?.name || '').startsWith('Ventas')
    const isAdvance = (selectedCategory?.name || '') === 'Deuda / Anticipo Interno'

    useEffect(() => {
        async function loadDebts() {
            if (!isTraspaso || !traspassAgencyId) { setTraspassDebts([]); return }
            try {
                const res = await AdminService.listTraspassDebts(traspassAgencyId, movementType, debtsFrom, debtsTo)
                setTraspassDebts(res.data)
            } catch (error) {
                console.log(error)
            }
        }
        loadDebts()
        setSelectedLinks({})
        setSelectedExistingPayments({})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTraspaso, traspassAgencyId, movementType, debtsFrom, debtsTo])

    // Búsqueda de pagos (`payments`) ya registrados en el sistema, para vincular en vez de crear uno nuevo.
    useEffect(() => {
        if (!isTraspaso) return
        const q = existingPaymentSearch.trim()
        if (q.length < 2) { setExistingPaymentResults([]); return }
        const timeout = setTimeout(async () => {
            try {
                const res = await AdminService.searchExistingPayments(q)
                setExistingPaymentResults(res.data)
            } catch (error) {
                console.log(error)
            }
        }, 300)
        return () => clearTimeout(timeout)
    }, [isTraspaso, existingPaymentSearch])

    function toggleExistingPayment(payment: ExistingPayment) {
        setSelectedExistingPayments(prev => {
            const next = { ...prev }
            if (next[payment.id]) delete next[payment.id]
            else next[payment.id] = payment
            return next
        })
        setIsExistingPaymentSearchOpen(false)
        setExistingPaymentSearch('')
    }

    // Búsqueda de reserva para vincular una Venta a su pago real (payment_reservations).
    useEffect(() => {
        if (!isVenta) return
        const q = reservationSearch.trim()
        if (q.length < 2) { setReservationResults([]); return }
        const timeout = setTimeout(async () => {
            try {
                const res = await AdminService.searchReservations(q)
                setReservationResults(res.data)
            } catch (error) {
                console.log(error)
            }
        }, 300)
        return () => clearTimeout(timeout)
    }, [isVenta, reservationSearch])

    useEffect(() => {
        async function loadPayments() {
            if (!selectedReservation) { setReservationPayments([]); return }
            try {
                const res = isGroupMode && selectedReservation.group_id
                    ? await AdminService.listGroupPayments(selectedReservation.group_id)
                    : await AdminService.listReservationPayments(selectedReservation.id)
                setReservationPayments((res.data || []).filter((p: any) => !p.authorized))
            } catch (error) {
                console.log(error)
            }
        }
        loadPayments()
    }, [selectedReservation, isGroupMode])

    useEffect(() => {
        async function loadGroupSummary() {
            if (!isGroupMode || !selectedReservation?.group_id) { setGroupSummary(null); return }
            try {
                const res = await AdminService.getGroupSummary(selectedReservation.group_id)
                setGroupSummary(res.data)
            } catch (error) {
                console.log(error)
            }
        }
        loadGroupSummary()
    }, [isGroupMode, selectedReservation])

    function selectReservation(r: ReservationSearchResult) {
        setSelectedReservation(r)
        setIsReservationSearchOpen(false)
        setReservationSearch('')
        setPaymentReservationId('')
        setIsGroupMode(false)
        setVentaAmountTour(amount || '')
        setVentaAmountTickets('0')
    }

    const sortedAgencies = useMemo(() => [...agencies].sort((a, b) => a.name.localeCompare(b.name)), [agencies])
    const filteredAgencyOptions = useMemo(
        () => sortedAgencies.filter(a => a.name.toLowerCase().includes(agencySearch.trim().toLowerCase())),
        [sortedAgencies, agencySearch]
    )
    const selectedAgencyName = agencies.find(a => a.id === traspassAgencyId)?.name || ''

    function selectAgency(agency: Agency) {
        setTraspassAgencyId(agency.id)
        setIsAgencySearchOpen(false)
    }

    function toggleDebt(debt: TraspassDebt) {
        setSelectedLinks(prev => {
            const next = { ...prev }
            if (next[debt.target_id] !== undefined) delete next[debt.target_id]
            else next[debt.target_id] = { tour: debt.tour_remaining, tickets: debt.tickets_remaining }
            return next
        })
    }

    function updateLinkAmount(target_id: string, field: 'tour' | 'tickets', value: string) {
        setSelectedLinks(prev => ({
            ...prev,
            [target_id]: { ...prev[target_id], [field]: value === '' ? 0 : Number(value) }
        }))
    }

    const existingPaymentsSum = Object.values(selectedExistingPayments).reduce((a, p) => a + p.amount + p.amount_tickets, 0)
    const selectedLinksSum = Object.values(selectedLinks).reduce((a, l) => a + l.tour + l.tickets, 0) + existingPaymentsSum

    function selectPendingPayment(id: string) {
        setPendingPaymentId(id)
        const pp = pendingPayments.find(p => p.id === id)
        if (pp) {
            setCategoryId(pp.category_id)
            setMovementType('egreso')
            setCounterpartName(pp.counterpart_name)
        }
    }

    const selectedPendingPayment = useMemo(
        () => pendingPayments.find(p => p.id === pendingPaymentId),
        [pendingPayments, pendingPaymentId]
    )

    function closeModal() {
        setIsCreateCashMovementModalOpen(false)
        setCreateCashMovementPrefill(undefined)
    }

    async function register(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        if (isTraspaso && !traspassAgencyId) {
            alert('Selecciona la agencia del traspaso')
            return
        }
        if (isTraspaso && !isGeneralAbono && (Object.keys(selectedLinks).length > 0 || Object.keys(selectedExistingPayments).length > 0) && Math.abs(selectedLinksSum - Number(amount)) > 0.01) {
            alert('La suma de los pasajeros/traspasos/pagos seleccionados no calza con el monto ingresado')
            return
        }
        if (isVenta && selectedReservation && !paymentReservationId) {
            const sum = Number(ventaAmountTour || 0) + Number(ventaAmountTickets || 0)
            if (Math.abs(sum - Number(amount)) > 0.01) {
                alert('La suma de monto tour + entradas no calza con el monto ingresado')
                return
            }
        }
        try {
            setIsLoading(true)
            const formData = new FormData();
            formData.append('account_id', accountId)
            formData.append('movement_type', movementType)
            formData.append('amount', amount)
            formData.append('category_id', categoryId)
            const effectiveCounterpartName = isTraspaso
                ? (agencies.find(a => a.id === traspassAgencyId)?.name || '')
                : isAdvance && advanceSellerId
                    ? (sellers.find(s => s.id === advanceSellerId)?.name || '')
                    : counterpartName
            if (effectiveCounterpartName) formData.append('counterpart_name', effectiveCounterpartName)
            if (isTraspaso && traspassAgencyId) formData.append('counterpart_agency_id', traspassAgencyId)
            if (isAdvance && advanceSellerId) formData.append('counterpart_seller_id', advanceSellerId)
            if (detail) formData.append('detail', detail)
            if (executionDate) formData.append('execution_date', executionDate)
            if (photo) formData.append('photo', photo)
            if (shiftId) formData.append('shift_id', shiftId)
            if (pendingPaymentId) formData.append('pending_payment_id', pendingPaymentId)
            if (tripCostDebtId) formData.append('trip_cost_debt_id', tripCostDebtId)
            if (prefill?.bank_statement_line_id) formData.append('bank_statement_line_id', prefill.bank_statement_line_id)
            if (isVenta && selectedReservation) {
                if (!isGroupMode) formData.append('reservation_id', selectedReservation.id)
                if (paymentReservationId) {
                    formData.append('payment_reservation_id', paymentReservationId)
                } else if (isGroupMode && selectedReservation.group_id) {
                    formData.append('new_payment_reservation[group_id]', selectedReservation.group_id)
                    formData.append('new_payment_reservation[amount]', ventaAmountTour || '0')
                    formData.append('new_payment_reservation[amount_tickets]', ventaAmountTickets || '0')
                } else {
                    formData.append('new_payment_reservation[reservation_id]', selectedReservation.id)
                    formData.append('new_payment_reservation[amount]', ventaAmountTour || '0')
                    formData.append('new_payment_reservation[amount_tickets]', ventaAmountTickets || '0')
                }
            }
            if (isTraspaso && isGeneralAbono) {
                formData.append('traspass_links[0][is_general]', 'true')
                formData.append('traspass_links[0][amount_tour]', amount)
                formData.append('traspass_links[0][amount_tickets]', '0')
            }
            if (isTraspaso && !isGeneralAbono && (Object.keys(selectedLinks).length > 0 || Object.keys(selectedExistingPayments).length > 0)) {
                // Notación de corchetes: el middleware formidable.ts compartido convierte con
                // String(value) cualquier campo simple, lo que corrompe un JSON.stringify de un
                // array de objetos (queda como "[object Object]"). Los corchetes usan su propia
                // lógica de armado de objeto anidado y sí funcionan bien.
                let i = 0
                const links = traspassDebts.filter(d => selectedLinks[d.target_id] !== undefined)
                links.forEach(d => {
                    const key = d.target_type === 'reservation' ? 'reservation_id' : 're_traspaso_id'
                    formData.append(`traspass_links[${i}][${key}]`, d.target_id)
                    formData.append(`traspass_links[${i}][amount_tour]`, String(selectedLinks[d.target_id].tour))
                    formData.append(`traspass_links[${i}][amount_tickets]`, String(selectedLinks[d.target_id].tickets))
                    i++
                })
                // Pagos ya existentes: el monto se toma tal cual está en `payments`, no se pueden editar.
                Object.values(selectedExistingPayments).forEach(p => {
                    formData.append(`traspass_links[${i}][payment_id]`, p.id)
                    formData.append(`traspass_links[${i}][amount_tour]`, String(p.amount))
                    formData.append(`traspass_links[${i}][amount_tickets]`, String(p.amount_tickets))
                    i++
                })
            }
            await AdminService.createCashMovement(formData)
            window.location.reload()
        } catch (error: any) {
            alert(typeof error?.response?.data === 'string' ? error.response.data : 'No se pudo registrar el movimiento')
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Main>
            <Modal onSubmit={register}>
                <h1>{tripCostDebtId ? 'Pagar deuda de trip' : 'Nuevo movimiento'}</h1>
                {!tripCostDebtId && (
                    <div className="type-toggle">
                        <button
                            type="button"
                            className={movementType === 'ingreso' ? 'active ingreso' : ''}
                            onClick={() => { setMovementType('ingreso'); setCategoryId(''); setPendingPaymentId('') }}
                        >
                            Ingreso
                        </button>
                        <button
                            type="button"
                            className={movementType === 'egreso' ? 'active egreso' : ''}
                            onClick={() => { setMovementType('egreso'); setCategoryId('') }}
                        >
                            Egreso
                        </button>
                    </div>
                )}
                {!tripCostDebtId && movementType === 'egreso' && pendingPayments.length > 0 && (
                    <div className="pending-toggle">
                        <label>¿Es un abono a un pago pendiente?</label>
                        <Select
                            defaultNull
                            value={pendingPaymentId}
                            options={pendingPayments.map(p => ({
                                value: p.id,
                                name: `${p.counterpart_name} — saldo $${(p.total_amount - p.paid_amount).toLocaleString('es-CL')}`
                            }))}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => selectPendingPayment(e.target.value)}
                        />
                        {selectedPendingPayment && (
                            <div className="pending-summary">
                                Total: ${selectedPendingPayment.total_amount.toLocaleString('es-CL')} · Pagado: ${selectedPendingPayment.paid_amount.toLocaleString('es-CL')} · Saldo: ${(selectedPendingPayment.total_amount - selectedPendingPayment.paid_amount).toLocaleString('es-CL')}
                            </div>
                        )}
                    </div>
                )}
                <GeneralInput
                    label="Monto"
                    placeholder="10.000"
                    type="number"
                    required
                    value={amount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                    style={{ margin: '15px 0' }}
                />
                <GeneralInput
                    label="Fecha del movimiento"
                    type="date"
                    required
                    value={executionDate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setExecutionDate(e.target.value)}
                    style={{ marginBottom: '15px' }}
                />
                <Select
                    label="Cuenta"
                    required
                    defaultNull
                    value={accountId}
                    options={accounts.map(a => ({ value: a.id, name: a.name }))}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setAccountId(e.target.value)}
                    style={{ marginBottom: '15px' }}
                />
                <Select
                    label="Categoría"
                    required
                    defaultNull
                    value={categoryId}
                    options={filteredCategories.map(c => ({ value: c.id, name: c.name }))}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setCategoryId(e.target.value)}
                    style={{ marginBottom: '15px' }}
                />
                {isTraspaso && (
                    <div className="traspaso-links">
                        <div className="agency-search">
                            <label>Agencia</label>
                            <input
                                type="text"
                                placeholder="Buscar agencia..."
                                value={isAgencySearchOpen ? agencySearch : selectedAgencyName}
                                onFocus={() => { setIsAgencySearchOpen(true); setAgencySearch('') }}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setAgencySearch(e.target.value)}
                                onBlur={() => setTimeout(() => setIsAgencySearchOpen(false), 150)}
                            />
                            {isAgencySearchOpen && (
                                <ul className="agency-options">
                                    {filteredAgencyOptions.length === 0 && <li className="empty">Sin resultados</li>}
                                    {filteredAgencyOptions.map(a => (
                                        <li key={a.id} onMouseDown={() => selectAgency(a)}>{a.name}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {traspassAgencyId && (
                            <>
                                <label className="general-abono-toggle">
                                    <input
                                        type="checkbox"
                                        checked={isGeneralAbono}
                                        onChange={e => setIsGeneralAbono(e.target.checked)}
                                    />
                                    Es un abono general (no corresponde a un pasajero/traspaso específico — abona contra la deuda total de la agencia)
                                </label>
                                {!isGeneralAbono && (
                                <>
                                <div className="dates-range">
                                    <label>
                                        Desde
                                        <input type="date" value={debtsFrom} onChange={e => setDebtsFrom(e.target.value)} />
                                    </label>
                                    <label>
                                        Hasta
                                        <input type="date" value={debtsTo} onChange={e => setDebtsTo(e.target.value)} />
                                    </label>
                                </div>
                                <label>¿A qué pasajero(s)/traspaso(s) corresponde?</label>
                                {traspassDebts.length === 0 && <p className="empty">No hay deudas pendientes con esta agencia en el rango de fechas seleccionado</p>}
                                <ul>
                                    {traspassDebts.map(debt => {
                                        const isChecked = selectedLinks[debt.target_id] !== undefined
                                        return (
                                            <li key={debt.target_id}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => toggleDebt(debt)}
                                                    />
                                                    {debt.passenger_name} — {debt.tour_id} ({debt.tour_date}) — saldo tour ${debt.tour_remaining.toLocaleString('es-CL')} / entradas ${debt.tickets_remaining.toLocaleString('es-CL')}
                                                </label>
                                                {isChecked && (
                                                    <div className="link-amounts">
                                                        <label>
                                                            Tour
                                                            <input
                                                                className="link-amount"
                                                                type="number"
                                                                min={0}
                                                                max={debt.tour_remaining}
                                                                step="any"
                                                                value={selectedLinks[debt.target_id].tour}
                                                                onChange={e => updateLinkAmount(debt.target_id, 'tour', e.target.value)}
                                                            />
                                                        </label>
                                                        <label>
                                                            Entradas
                                                            <input
                                                                className="link-amount"
                                                                type="number"
                                                                min={0}
                                                                max={debt.tickets_remaining}
                                                                step="any"
                                                                value={selectedLinks[debt.target_id].tickets}
                                                                onChange={e => updateLinkAmount(debt.target_id, 'tickets', e.target.value)}
                                                            />
                                                        </label>
                                                    </div>
                                                )}
                                            </li>
                                        )
                                    })}
                                </ul>
                                <label>¿O ya existe el pago registrado en el sistema?</label>
                                <div className="existing-payment-search">
                                    <input
                                        type="text"
                                        placeholder="Buscar pasajero del pago ya registrado..."
                                        value={existingPaymentSearch}
                                        onFocus={() => setIsExistingPaymentSearchOpen(true)}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => setExistingPaymentSearch(e.target.value)}
                                        onBlur={() => setTimeout(() => setIsExistingPaymentSearchOpen(false), 150)}
                                    />
                                    {isExistingPaymentSearchOpen && existingPaymentSearch.trim().length >= 2 && (
                                        <ul className="existing-payment-options">
                                            {existingPaymentResults.length === 0 && <li className="empty">Sin resultados</li>}
                                            {existingPaymentResults.map(p => (
                                                <li key={p.id} onMouseDown={() => toggleExistingPayment(p)}>
                                                    {p.passenger_name} — {p.tour_id} ({p.tour_date}) — ${(p.amount + p.amount_tickets).toLocaleString('es-CL')}{p.remarks ? ` — ${p.remarks}` : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                {Object.keys(selectedExistingPayments).length > 0 && (
                                    <ul className="selected-existing-payments">
                                        {Object.values(selectedExistingPayments).map(p => (
                                            <li key={p.id}>
                                                <span>{p.passenger_name} — {p.tour_id} ({p.tour_date}) — ${(p.amount + p.amount_tickets).toLocaleString('es-CL')}</span>
                                                <button type="button" onClick={() => toggleExistingPayment(p)}>Quitar</button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {(Object.keys(selectedLinks).length > 0 || Object.keys(selectedExistingPayments).length > 0) && (
                                    <div className="links-summary">
                                        Suma seleccionada: ${selectedLinksSum.toLocaleString('es-CL')}
                                        {amount && Math.abs(selectedLinksSum - Number(amount)) > 0.01 &&
                                            <span className="warning"> — no calza con el monto ingresado (${Number(amount).toLocaleString('es-CL')})</span>
                                        }
                                    </div>
                                )}
                                </>
                                )}
                            </>
                        )}
                    </div>
                )}
                {isVenta && (
                    <div className="venta-link">
                        <div className="reservation-search">
                            <label>Vincular a una reserva (opcional)</label>
                            <input
                                type="text"
                                placeholder="Buscar pasajero..."
                                value={isReservationSearchOpen ? reservationSearch : (selectedReservation ? `${selectedReservation.passenger_name} — ${selectedReservation.tour_id}` : '')}
                                onFocus={() => { setIsReservationSearchOpen(true); setReservationSearch('') }}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setReservationSearch(e.target.value)}
                                onBlur={() => setTimeout(() => setIsReservationSearchOpen(false), 150)}
                            />
                            {isReservationSearchOpen && reservationSearch.trim().length >= 2 && (
                                <ul className="reservation-options">
                                    {reservationResults.length === 0 && <li className="empty">Sin resultados</li>}
                                    {reservationResults.map(r => (
                                        <li key={r.id} onMouseDown={() => selectReservation(r)}>
                                            {r.passenger_name} — {r.tour_id} ({r.reservation_date}) — saldo ${(r.price + r.tickets - r.paid).toLocaleString('es-CL')}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {selectedReservation && (
                            <>
                                {selectedReservation.group_id && (
                                    <label className="group-toggle">
                                        <input
                                            type="checkbox"
                                            checked={isGroupMode}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => setIsGroupMode(e.target.checked)}
                                        />
                                        Este pago corresponde a todo el grupo, no solo a {selectedReservation.passenger_name}
                                    </label>
                                )}
                                {isGroupMode && groupSummary ? (
                                    <div className="reservation-summary">
                                        Grupo ({groupSummary.passengers.length} pasajero{groupSummary.passengers.length !== 1 ? 's' : ''}): {groupSummary.passengers.map(p => `${p.name} (${p.tour_id})${p.is_original ? '' : ' — agregada después'}`).join(', ')}
                                        <br />
                                        Total: ${(groupSummary.total_price + groupSummary.total_tickets).toLocaleString('es-CL')} · Pagado: ${groupSummary.paid.toLocaleString('es-CL')} · Saldo: ${(groupSummary.total_price + groupSummary.total_tickets - groupSummary.paid).toLocaleString('es-CL')}
                                    </div>
                                ) : (
                                    <div className="reservation-summary">
                                        Total: ${(selectedReservation.price + selectedReservation.tickets).toLocaleString('es-CL')} · Pagado: ${selectedReservation.paid.toLocaleString('es-CL')} · Saldo: ${(selectedReservation.price + selectedReservation.tickets - selectedReservation.paid).toLocaleString('es-CL')}
                                    </div>
                                )}
                                {reservationPayments.length > 0 && (
                                    <Select
                                        label="¿Confirmar un pago ya creado (por el vendedor o por ti)?"
                                        value={paymentReservationId}
                                        options={[
                                            { value: '', name: '-- Crear un pago nuevo --' },
                                            ...reservationPayments.map((p: any) => ({
                                                value: p.id,
                                                name: `$${(Number(p.amount) + Number(p.amount_tickets || 0)).toLocaleString('es-CL')} — ${p.remarks || 'sin comentario'}`
                                            }))
                                        ]}
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setPaymentReservationId(e.target.value)}
                                        style={{ margin: '10px 0' }}
                                    />
                                )}
                                {!paymentReservationId && (
                                    <div className="venta-amounts">
                                        <label>
                                            Monto tour
                                            <input type="number" min={0} step="any" value={ventaAmountTour} onChange={e => setVentaAmountTour(e.target.value)} />
                                        </label>
                                        <label>
                                            Monto entradas
                                            <input type="number" min={0} step="any" value={ventaAmountTickets} onChange={e => setVentaAmountTickets(e.target.value)} />
                                        </label>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
                {/* Solo turnos de vendedor: un turno de Admin es period='FULL' (un mismo turno
                    cubre AM y PM), así que asignar un movimiento a uno no puede contar en un
                    cierre específico sin arriesgar contarlo dos veces — ver resolveShiftId en el
                    backend. */}
                {shifts.filter(s => s.role === 'seller').length > 0 && (
                    <Select
                        label="Turno (opcional)"
                        defaultNull
                        value={shiftId}
                        options={shifts.filter(s => s.role === 'seller').map(s => ({
                            value: s.id,
                            name: `Vendedor: ${s.person?.name || ''} (${s.period})`
                        }))}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setShiftId(e.target.value)}
                        style={{ marginBottom: '15px' }}
                    />
                )}
                {isAdvance && (
                    <Select
                        label="Vendedor"
                        required
                        defaultNull
                        value={advanceSellerId}
                        options={sellers.map(s => ({ value: s.id, name: s.name }))}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setAdvanceSellerId(e.target.value)}
                        style={{ marginBottom: '15px' }}
                    />
                )}
                {!isTraspaso && !isAdvance && !pendingPaymentId && (
                    <GeneralInput
                        label="Contraparte"
                        placeholder={filteredCategories.find(c => c.id === categoryId)?.counterpart_hint || 'Nombre'}
                        value={counterpartName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setCounterpartName(e.target.value)}
                        style={{ marginBottom: '15px' }}
                    />
                )}
                <GeneralInput
                    label="Detalle (opcional)"
                    value={detail}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setDetail(e.target.value)}
                    style={{ marginBottom: '15px' }}
                />
                <FileInput
                    label="Foto de respaldo"
                    style={{ margin: '10px 0' }}
                    value={photo}
                    capture="environment"
                    deleteFile={() => setPhoto(undefined)}
                    accept=".png, .jpg, .jpeg, .heic, .webp"
                    onChange={(newFile: File) => setPhoto(newFile)}
                />
                <div className="close" title="close" onClick={closeModal}>
                    <AiOutlineCloseCircle/>
                </div>
                <button type="submit" className="submit">Guardar movimiento</button>
            </Modal>
        </Main>
    )
}

const Main = styled.div`
z-index: 100;
position: fixed;
top: 0;
left: 0;
width: 100vw;
height: 100vh;
/* 100vh no descuenta la barra de Chrome en iOS (Safari sí) — con eso, este overlay queda más
   alto que lo realmente visible y el botón de guardar (al final del modal) queda fuera de
   pantalla sin forma de llegar a él. 100dvh sí la descuenta; se deja 100vh antes como respaldo
   para navegadores que no soporten dvh. */
height: 100dvh;
background-color: ${props => props.theme.blackTransparent};
`

const showUpModal = keyframes`
    from {
        top: 100%;
    }
    to {
        top: 50%;
    }
    `

const showUpMobile = keyframes`
    from {
        bottom: -100%;
    }
    to {
        bottom: 0;
    }
    `

const Modal = styled.form`
width: 80%;
max-height: 90%;
overflow: hidden;
overflow-y: auto;
max-width: 500px;
position: absolute;
top: 50%;
left: 50%;
transform: translate(-50%, -50%);
background-color: ${props => props.theme.white};
box-shadow: 0 0 20px ${props => props.theme.blackTransparent};
padding: 20px;
padding-top: 50px;
animation: ${showUpModal} .3s ease forwards;
${mobile(css`
    width: 100%;
    max-width: 100%;
    max-height: 90%;
    top: auto;
    left: auto;
    transform: initial;
    border-radius: 15px 15px 0 0;
    animation: ${showUpMobile} .3s ease forwards;
`)}
h1 {
    font-size: 22px;
    margin-bottom: 20px;
    font-weight: 400;
}
.type-toggle {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
    button {
        flex: 1;
        padding: 15px;
        font-size: 16px;
        border-radius: 10px;
        border: 2px solid ${props => props.theme.gray};
        background-color: ${props => props.theme.white};
        cursor: pointer;
    }
    button.active.ingreso {
        border-color: ${props => props.theme.blue};
        background-color: ${props => props.theme.blue};
        color: ${props => props.theme.white};
    }
    button.active.egreso {
        border-color: ${props => props.theme.lightRed};
        background-color: ${props => props.theme.lightRed};
        color: ${props => props.theme.white};
    }
}
.pending-toggle {
    margin-bottom: 15px;
    padding: 10px;
    border-radius: 8px;
    background-color: ${props => props.theme.whiteBone};
    label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 8px;
    }
    .pending-summary {
        margin-top: 8px;
        font-size: 13px;
        color: ${props => props.theme.gray};
    }
}
.traspaso-links {
    margin-bottom: 15px;
    padding: 10px;
    border-radius: 8px;
    background-color: ${props => props.theme.whiteBone};
    > label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 8px;
    }
    .empty {
        font-size: 13px;
        color: ${props => props.theme.gray};
    }
    .general-abono-toggle {
        display: flex !important;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        font-weight: 500;
        margin: 10px 0;
        cursor: pointer;
        input {
            width: auto;
            flex-shrink: 0;
        }
    }
    .agency-search {
        position: relative;
        margin-bottom: 10px;
        label {
            display: block;
            font-size: 13px;
            font-weight: 600;
            margin-bottom: 6px;
        }
        input {
            width: 100%;
            padding: 8px 10px;
            border-radius: 6px;
            border: 1px solid ${props => props.theme.gray};
            font-size: 14px;
            box-sizing: border-box;
        }
        .agency-options {
            position: absolute;
            z-index: 20;
            top: 100%;
            left: 0;
            right: 0;
            max-height: 200px;
            overflow-y: auto;
            list-style: none;
            background: ${props => props.theme.white};
            border: 1px solid ${props => props.theme.gray};
            border-radius: 6px;
            margin-top: 4px;
            box-shadow: 0 3px 8px ${props => props.theme.blackTransparent};
            li {
                padding: 8px 10px;
                font-size: 13px;
                cursor: pointer;
            }
            li:hover {
                background: ${props => props.theme.whiteBone};
            }
            li.empty {
                cursor: default;
                color: ${props => props.theme.gray};
            }
            li.empty:hover {
                background: none;
            }
        }
    }
    .dates-range {
        display: flex;
        gap: 10px;
        margin-bottom: 10px;
        label {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;
            font-size: 12px;
            font-weight: 600;
        }
        input {
            padding: 6px;
            border-radius: 6px;
            border: 1px solid ${props => props.theme.gray};
            font-size: 13px;
        }
    }
    ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 6px;
        max-height: 220px;
        overflow-y: auto;
        li {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
        }
        li > label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            cursor: pointer;
            flex: 1;
        }
        .link-amounts {
            display: flex;
            gap: 10px;
            width: 100%;
            padding-left: 24px;
            label {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                color: ${props => props.theme.gray};
            }
        }
        .link-amount {
            width: 90px;
            padding: 4px 6px;
            border-radius: 6px;
            border: 1px solid ${props => props.theme.gray};
            font-size: 13px;
        }
    }
    .existing-payment-search {
        position: relative;
        margin-top: 10px;
        margin-bottom: 10px;
        input {
            width: 100%;
            padding: 8px 10px;
            border-radius: 6px;
            border: 1px solid ${props => props.theme.gray};
            font-size: 14px;
            box-sizing: border-box;
        }
        .existing-payment-options {
            position: absolute;
            z-index: 20;
            top: 100%;
            left: 0;
            right: 0;
            max-height: 200px;
            overflow-y: auto;
            list-style: none;
            background: ${props => props.theme.white};
            border: 1px solid ${props => props.theme.gray};
            border-radius: 6px;
            margin-top: 4px;
            box-shadow: 0 3px 8px ${props => props.theme.blackTransparent};
            li {
                padding: 8px 10px;
                font-size: 13px;
                cursor: pointer;
            }
            li:hover {
                background: ${props => props.theme.whiteBone};
            }
            li.empty {
                cursor: default;
                color: ${props => props.theme.gray};
            }
            li.empty:hover {
                background: none;
            }
        }
    }
    .selected-existing-payments {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 10px;
        li {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            font-size: 13px;
            background: ${props => props.theme.white};
            border-radius: 6px;
            padding: 6px 10px;
        }
        button {
            border: none;
            background: none;
            color: ${props => props.theme.lightRed};
            cursor: pointer;
            font-size: 12px;
        }
    }
    .links-summary {
        margin-top: 8px;
        font-size: 13px;
        font-weight: 600;
        .warning {
            color: ${props => props.theme.lightRed};
            font-weight: 400;
        }
    }
}
.venta-link {
    margin-bottom: 15px;
    padding: 10px;
    border-radius: 8px;
    background-color: ${props => props.theme.whiteBone};
    .reservation-search {
        position: relative;
        label {
            display: block;
            font-size: 13px;
            font-weight: 600;
            margin-bottom: 6px;
        }
        input {
            width: 100%;
            padding: 8px 10px;
            border-radius: 6px;
            border: 1px solid ${props => props.theme.gray};
            font-size: 14px;
            box-sizing: border-box;
        }
        .reservation-options {
            position: absolute;
            z-index: 20;
            top: 100%;
            left: 0;
            right: 0;
            max-height: 200px;
            overflow-y: auto;
            list-style: none;
            background: ${props => props.theme.white};
            border: 1px solid ${props => props.theme.gray};
            border-radius: 6px;
            margin-top: 4px;
            box-shadow: 0 3px 8px ${props => props.theme.blackTransparent};
            li {
                padding: 8px 10px;
                font-size: 13px;
                cursor: pointer;
            }
            li:hover {
                background: ${props => props.theme.whiteBone};
            }
            li.empty {
                cursor: default;
                color: ${props => props.theme.gray};
            }
            li.empty:hover {
                background: none;
            }
        }
    }
    .reservation-summary {
        margin-top: 10px;
        font-size: 13px;
        font-weight: 600;
    }
    .group-toggle {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 10px;
        font-size: 13px;
        cursor: pointer;
        input {
            cursor: pointer;
        }
    }
    .venta-amounts {
        display: flex;
        gap: 10px;
        margin-top: 10px;
        label {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;
            font-size: 12px;
            font-weight: 600;
            color: ${props => props.theme.gray};
        }
        input {
            padding: 6px;
            border-radius: 6px;
            border: 1px solid ${props => props.theme.gray};
            font-size: 13px;
        }
    }
}
.close {
    position: absolute;
    right: 20px;
    top: 20px;
    font-size: 22px;
    cursor: pointer;
}
.submit {
    width: 100%;
    padding: 15px;
    font-size: 17px;
    margin-top: 10px;
    background-color: ${props => props.theme.blue};
    color: ${props => props.theme.white};
    border: none;
    border-radius: 8px;
    cursor: pointer;
}
`
